import { memo } from 'react'
import { FAIXAS, faixaDe } from '../faixas'
import { TETO, TRACO, FOLGA, CX, CY, RAIO, arco, pontaDaAgulha } from '../geometria'

/**
 * Velocímetro — porte direto da `função velocimetro` do protótipo. A geometria
 * vive em `../geometria.ts`, que é testável sem JSX; aqui só se desenha.
 *
 * SEM TEXTO (decisão de 09/10): só o arco colorido e a agulha. O valor vendido
 * já aparece logo abaixo, em reais. Isso deixa a cor sozinha carregando a
 * situação, e cerca de 8 % dos homens têm alguma deficiência de visão de cor —
 * daí as três compensações, que devem ser mantidas:
 *
 *   1. o `aria-label` continua dizendo a situação (não ocupa espaço);
 *   2. a posição da agulha informa por si, independentemente de cor;
 *   3. a tabela de valores do admin tem a coluna "Situação" por escrito.
 *
 * `memo` com comparação pela proporção arredondada: o componente NÃO deve ser
 * recriado a cada atualização de valor. No telão, redesenhar o SVG a cada
 * pedido que entra é exatamente o piscar que o requisito proíbe — a agulha é um
 * `<line>` cujas coordenadas mudam, e o React reconcilia só o atributo.
 */

type Props = {
  /** vendido / meta. Só posiciona a agulha e escolhe a cor; nunca vira texto. */
  proporcao: number
  /** `vel-geral` | `vel-card` | `vel-simples` | `vel-marca-grande` */
  classe: string
}

function VelocimetroBase({ proporcao, classe }: Props) {
  const faixa = faixaDe(proporcao)
  const [ax, ay] = pontaDaAgulha(proporcao)

  return (
    /* viewBox com 75 de ALTURA: as pontas do arco chegam a y=71 e, com o traço
       de 7, a 74,5. Com a altura antiga (68) as duas pontas saíam cortadas. */
    <svg viewBox="0 0 100 75" className={classe} role="img" aria-label={faixa.rotulo}>
      {/* trilha por baixo, cobrindo a escala inteira */}
      <path d={arco(0, TETO, RAIO)} fill="none" stroke="var(--trilha)"
            strokeWidth={TRACO} strokeLinecap="butt" />

      {/* as três faixas, com a folga entre vizinhas */}
      <path d={arco(0, FAIXAS[0]!.ate - FOLGA, RAIO)}
            fill="none" stroke={FAIXAS[0]!.hex} strokeWidth={TRACO} strokeLinecap="butt" />
      <path d={arco(FAIXAS[0]!.ate + FOLGA, FAIXAS[1]!.ate - FOLGA, RAIO)}
            fill="none" stroke={FAIXAS[1]!.hex} strokeWidth={TRACO} strokeLinecap="butt" />
      <path d={arco(FAIXAS[1]!.ate + FOLGA, TETO, RAIO)}
            fill="none" stroke={FAIXAS[2]!.hex} strokeWidth={TRACO} strokeLinecap="butt" />

      {/* a agulha: só estes dois atributos mudam quando o valor muda */}
      <line x1={CX} y1={CY} x2={ax} y2={ay}
            stroke="var(--texto)" strokeWidth={2.6} strokeLinecap="round" />
      <circle cx={CX} cy={CY} r={3.6} fill="var(--texto)" />
    </svg>
  )
}

/* Repintar com a mesma proporção não muda um pixel. O arredondamento em 4 casas
   corta repintura por ruído de ponto flutuante sem perder movimento visível —
   num arco de 240°, 0,0001 da escala é bem menos de um décimo de grau. */
export const Velocimetro = memo(
  VelocimetroBase,
  (a, b) =>
    a.classe === b.classe &&
    Math.round(a.proporcao * 1e4) === Math.round(b.proporcao * 1e4),
)
